import ItemSelectorModal from "@/src/components/action-modals/item-selection/item-selection-modal";
import { SelectableItem } from "@/src/components/action-modals/item-selection/item-row";
import FeatureRequestBoard from "@/src/components/feature-requests/feature-request-board";
import FeatureRequestHeader, {
  FEATURE_TABS,
  FeatureTab,
} from "@/src/components/feature-requests/feature-request-header";
import FeatureRequestModal from "@/src/components/feature-requests/feature-request-modal";
import { useAuth } from "@/src/contexts/auth.context";
import { usePermissions } from "@/src/hooks/use-permissions";
import { FeatureRequest } from "@/src/models/feature-request.model";
import { useGetUsers } from "@/src/services/api/groups/groups.hooks";
import { FeatureRequestScope } from "@/src/services/feature-request/feature-request.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { Monitor } from "iconsax-react-nativejs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Text, useWindowDimensions, View } from "react-native";

const SEARCH_DEBOUNCE_MS = 300;

/** The board needs room for its columns; phones get a notice instead. */
const DesktopOnlyNotice = () => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();

  return (
    <View className="flex-1 items-center justify-center gap-3 p-6 bg-chalk rounded-2xl">
      <Monitor size={48} color={themeVars["--color-ash-1"] as string} />
      <Text className="text-base font-semibold text-center text-onyx">
        {t("LABEL_FEATURE_REQUESTS_DESKTOP_ONLY")}
      </Text>
      <Text className="text-sm text-center text-slate">
        {t("HINT_FEATURE_REQUESTS_DESKTOP_ONLY")}
      </Text>
    </View>
  );
};

export default function FeatureRequestsScreen() {
  const { width } = useWindowDimensions();
  const { user } = useAuth();
  const { isSuperAdmin } = usePermissions();

  const [activeTab, setActiveTab] = useState<FeatureTab>(
    "LABEL_TAB_MY_FEATURES",
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [creators, setCreators] = useState<SelectableItem[]>([]);
  const [showCreatorPicker, setShowCreatorPicker] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<FeatureRequest | null>(
    null,
  );

  const [userSearch, setUserSearch] = useState("");
  const {
    data: usersData,
    fetchNextPage: fetchNextUsersPage,
    hasNextPage: usersHasNextPage,
    isFetchingNextPage: usersIsFetchingNextPage,
  } = useGetUsers(userSearch);
  const users = useMemo(
    () => usersData?.pages.flatMap((page) => page.users) ?? [],
    [usersData],
  );

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  // Members only ever see their own requests; "All Features" is a super-admin
  // view, same visibility rule as wiki feedback.
  const tabs = isSuperAdmin ? FEATURE_TABS : FEATURE_TABS.slice(0, 1);
  const showingAll = isSuperAdmin && activeTab === "LABEL_TAB_ALL_FEATURES";

  const scope: FeatureRequestScope = useMemo(
    () => ({
      createdBy: showingAll
        ? creators.map((c) => c.itemId)
        : user?.itemId
          ? [user.itemId]
          : [],
      search: debouncedSearch,
    }),
    [showingAll, creators, user?.itemId, debouncedSearch],
  );

  // Without a user id "My Features" would widen to everyone's requests.
  const boardEnabled = showingAll || !!user?.itemId;

  const canEditSelected =
    !!selectedRequest &&
    (isSuperAdmin || selectedRequest.CreatedBy === user?.itemId);

  if (width < 768) {
    return (
      <View className="flex-1 bg-obsidian">
        <DesktopOnlyNotice />
      </View>
    );
  }

  return (
    <View className="flex-1 gap-4 bg-obsidian">
      <FeatureRequestHeader
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        search={search}
        onSearchChange={setSearch}
        onCreatedByPress={showingAll ? () => setShowCreatorPicker(true) : undefined}
        createdByCount={showingAll ? creators.length : 0}
        onCreatePress={() => setShowCreateModal(true)}
      />

      <FeatureRequestBoard
        scope={scope}
        enabled={boardEnabled}
        onCardPress={setSelectedRequest}
      />

      <FeatureRequestModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      <FeatureRequestModal
        visible={!!selectedRequest}
        request={selectedRequest}
        canEdit={canEditSelected}
        onClose={() => setSelectedRequest(null)}
      />

      <ItemSelectorModal
        items={users}
        selectedItems={creators}
        visible={showCreatorPicker}
        titleKey="LABEL_CREATED_BY"
        searchPlaceholderKey="LABEL_SEARCH_PEOPLE"
        confirmButtonKey="BTN_APPLY"
        confirmIcon={null}
        onClose={() => setShowCreatorPicker(false)}
        onConfirm={(_ids, items) => setCreators(items)}
        onEndReached={() => {
          if (usersHasNextPage && !usersIsFetchingNextPage) fetchNextUsersPage();
        }}
        isFetchingMore={usersIsFetchingNextPage}
        onSearchChange={setUserSearch}
        totalCount={usersData?.pages[0]?.count}
      />
    </View>
  );
}
